import React, { useRef, useState } from "react";
import { Maximize, X, Minimize, SquarePen } from "lucide-react";
import { AssistantProvider } from "~/components/assistant/AssistantContext";
import { ChatInput, type ChatInputHandle } from "~/components/assistant/ChatInput";
import { ConversationSelector } from "~/components/assistant/ConversationSelector";
import { IconButton } from "~/components/assistant/PopoverButton";
import { Sidebar } from "~/components/assistant/Sidebar";
import type { AssistantApi } from "~/components/assistant/api/AssistantApi";
import { useAssistant } from "~/components/assistant/hooks/useAssistant";
import { useScrollManagement } from "~/components/assistant/hooks/useScrollManagement";
import { useSkillsFeature } from "~/components/assistant/hooks/useSkillsFeature";
import type { MessageFeedbackCreate } from "~/components/assistant/types";
import { Tooltip, TooltipContent, TooltipTrigger } from "~/components/ui/tooltip";
import { useResponsive } from "~/hooks/useResponsive";
import { cn } from "~/lib/utils";
import { trackEvent } from "~/utils/monitoring";
import { ConversationViewer } from "./ConversationViewer";
import type { PresetPrompt } from "./PresetPrompts";
import { PresetPrompts } from "./PresetPrompts";

export interface AssistantCoreProps {
  /**
   * The API implementation for backend communication
   */
  api: AssistantApi;
  /**
   * Unique identifier for the context (e.g., datasetId, cohortId, projectId)
   */
  contextId: string;
  /**
   * Display title for the context (e.g., dataset name, cohort name)
   */
  contextTitle: string;
  /**
   * Display mode for the assistant
   */
  mode: "popover" | "embedded";
  /**
   * Optional CSS class name
   */
  className?: string;
  /**
   * Callback to toggle fullscreen mode (only used in popover mode)
   */
  onToggleFullscreen?: () => void;
  /**
   * Callback to close the assistant (only used in popover mode)
   */
  onClose?: () => void;
  /**
   * Whether the assistant is in fullscreen mode
   */
  isFullscreen?: boolean;
  /**
   * Whether to show debug information (defaults to false for user-facing UI, true for admin panels)
   */
  debugMode?: boolean;
  /**
   * Whether to show the sidebar with conversation history (defaults to true)
   */
  showSidebar?: boolean;
  /**
   * Whether to show preset prompts in empty state (defaults to true)
   */
  showPresetPrompts?: boolean;
  /**
   * Whether to show the tool selector in the input (defaults to true)
   */
  showToolSelector?: boolean;
  /**
   * Custom title for the empty state
   */
  emptyStateTitle?: string;
  /**
   * Custom description for the empty state
   */
  emptyStateDescription?: string;
  /**
   * Custom placeholder text for the input
   */
  inputPlaceholder?: string;
  /**
   * Callback invoked when filters are received from the filter builder agent.
   * Used to auto-apply filters to a cohort.
   */
  onFiltersReceived?: (filters: unknown) => void;
  /**
   * Optional custom content to render in the empty state (e.g., usage guide button)
   */
  optionalEmptyStateContent?: React.ReactNode;
  /**
   * Whether to enable the skills UI (MentionInput, SkillSelector, etc). Defaults to false.
   */
  enableSkills?: boolean;
  /**
   * Custom preset prompts to display in the empty state. Falls back to defaults if not provided.
   */
  presetPrompts?: PresetPrompt[];
  /**
   * Optional initial conversation ID to select on load (e.g., from URL params).
   */
  initialConversationId?: string | null;
  /**
   * Callback invoked whenever the current conversation ID changes.
   */
  onConversationChange?: (conversationId: string | null) => void;
  /** Optional callback invoked when a new conversation is created. */
  onNewConversation?: (conversationId: string) => void;
  /** Optional callback invoked when the user sends a message. */
  onMessageSent?: (conversationId: string, message: string) => void;
  /** Optional callback invoked when the assistant finishes streaming a response. */
  onMessageReceived?: (conversationId: string, messageId: string) => void;
  /** Optional callback invoked when the stream returns an error. */
  onError?: (conversationId: string, error: unknown) => void;
  /** Optional callback invoked when the user submits feedback on a message. */
  onFeedbackSubmitted?: (
    conversationId: string,
    messageId: string,
    feedback: MessageFeedbackCreate
  ) => void;
  /** Optional callback invoked when the user cancels an in-flight message. */
  onMessageCancelled?: (conversationId: string) => void;
  /** External MCP server IDs explicitly enabled by the dataset owner. Non-enabled external servers are hidden. */
  externalMcpAccessIds?: string[];
}

export function AssistantCore({
  api,
  contextId,
  contextTitle,
  mode,
  className,
  onToggleFullscreen,
  onClose,
  isFullscreen = false,
  debugMode = false,
  showSidebar = true,
  showPresetPrompts = true,
  showToolSelector = true,
  emptyStateTitle = "Explore this dataset",
  emptyStateDescription = "Ask questions, generate tables or charts, and compare data across tables.",
  inputPlaceholder,
  onFiltersReceived,
  optionalEmptyStateContent,
  enableSkills = false,
  presetPrompts,
  initialConversationId,
  onConversationChange,
  onNewConversation,
  onMessageSent,
  onMessageReceived: onMessageReceivedProp,
  onError,
  onFeedbackSubmitted,
  onMessageCancelled,
  externalMcpAccessIds = [],
}: AssistantCoreProps) {
  const { isMobile } = useResponsive();

  // Skills require both the prop and feature flag to be enabled
  const skillsFeatureFlag = useSkillsFeature();
  const skillsEnabled = enableSkills && skillsFeatureFlag;

  // Use the main assistant hook for all chat functionality
  const {
    messages,
    conversations,
    currentConversationId,
    isCreatingConversation,
    isLoadingHistory,
    isSendingMessage,
    isCancelling,
    mcpServers,
    selectedMcpServers,
    systemSkills,
    userSkills,
    isLoadingSkills,
    sendMessage,
    cancelMessage,
    createConversation,
    deleteConversation,
    selectConversation,
    renameConversation,
    toggleMcpServer,
    submitMessageFeedback,
    clearMessageFeedback,
    createSkill,
    updateSkill,
    deleteSkill,
    getSkill,
  } = useAssistant({
    api,
    contextId,
    contextTitle,
    debugMode,
    skillsEnabled,
    onFiltersReceived,
    initialConversationId,
    onConversationChange,
    onMessageReceived: onMessageReceivedProp,
    onStreamError: onError,
  });

  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const chatInputRef = useRef<ChatInputHandle>(null);

  const isPopoverMode = mode === "popover";

  const canSendMessage = !isSendingMessage && contextId && currentConversationId;

  // Scroll management
  const { scrollContainerRef, contentRef, agentMinHeight, handleMessageRef, markUserSubmitted } =
    useScrollManagement({
      messages,
      isLoadingHistory,
      currentConversationId,
    });

  // Event handlers
  const handleNewChat = async () => {
    if (!contextId) {
      return;
    }

    trackEvent("analysis_agent_new_chat_clicked", {
      dataset_id: contextId,
      context: "dataset",
    });

    const newConv = await createConversation("manual");
    onNewConversation?.(newConv.id);
  };

  const handleConversationSelect = (conversationId: string) => {
    // Don't do anything if selecting the same conversation
    if (conversationId === currentConversationId) {
      return;
    }

    selectConversation(conversationId);
  };

  const handleDeleteConversation = (conversationId: string, event: React.MouseEvent) => {
    deleteConversation(conversationId, event);
  };

  const handleRenameConversation = (conversationId: string, displayName: string) => {
    renameConversation(conversationId, displayName);
  };

  const handleMcpServerToggle = (serverId: string) => {
    toggleMcpServer(serverId);
  };

  const handleToggleSidebar = () => {
    const newCollapsed = !isSidebarCollapsed;
    setIsSidebarCollapsed(newCollapsed);

    trackEvent("analysis_agent_sidebar_toggled", {
      dataset_id: contextId || "Unknown",
      context: "dataset",
      collapsed: newCollapsed,
    });
  };

  const handleSendMessage = (message: string) => {
    // Mark for smooth scroll (vs instant for conversation switches)
    markUserSubmitted();

    if (currentConversationId) {
      onMessageSent?.(currentConversationId, message);
    }

    // Send message using the hook (handles optimistic updates and streaming)
    sendMessage(message);
  };

  const handleCancelMessage = async () => {
    if (!isSendingMessage) {
      return;
    }

    await cancelMessage();
    if (currentConversationId) {
      onMessageCancelled?.(currentConversationId);
    }
  };

  const handleSubmitMessageFeedback = async (
    messageId: string,
    feedback: MessageFeedbackCreate
  ) => {
    await submitMessageFeedback(messageId, feedback);
    if (currentConversationId) {
      onFeedbackSubmitted?.(currentConversationId, messageId, feedback);
    }
  };

  const handlePresetClick = (prompt: string) => {
    if (!canSendMessage) {
      return;
    }

    // Track the preset click event
    trackEvent("analysis_agent_preset_clicked", {
      dataset_id: contextId,
      context: "dataset",
      prompt,
    });

    // Paste into input for user to review/edit before sending
    chatInputRef.current?.insertText(prompt);
  };

  return (
    <div className={cn("flex h-full bg-white", isMobile ? "flex-col" : "flex-row", className)}>
      {showSidebar && !isMobile && (
        <Sidebar
          datasetTitle={contextTitle}
          conversations={conversations}
          currentConversationId={currentConversationId}
          onNewChat={handleNewChat}
          onSelect={handleConversationSelect}
          onDelete={handleDeleteConversation}
          onRename={handleRenameConversation}
          onToggle={handleToggleSidebar}
          isCollapsed={isSidebarCollapsed}
        />
      )}

      {/* Main chat area */}
      <div className={cn("flex flex-col h-full min-w-0", "w-full")}>
        {/* Header */}
        <div className="flex flex-row items-center justify-between px-4 pt-2 pb-0 relative shrink-0 w-full">
          {/* Left side - Conversation controls */}
          {showSidebar && isMobile ? (
            <div className="flex flex-row gap-0.5 items-center mb-2">
              <ConversationSelector
                conversations={conversations}
                currentConversationId={currentConversationId}
                isCreatingConversation={isCreatingConversation}
                onSelect={handleConversationSelect}
                onDelete={handleDeleteConversation}
                onRename={handleRenameConversation}
              />

              {/* New Chat Button */}
              <Tooltip delayDuration={700}>
                <TooltipTrigger asChild>
                  <IconButton
                    onClick={handleNewChat}
                    disabled={isCreatingConversation}
                    variant="ghost"
                    aria-label="New chat"
                  >
                    <SquarePen className="size-4.5 text-gray-600" />
                  </IconButton>
                </TooltipTrigger>
                <TooltipContent>
                  <p>New Chat</p>
                </TooltipContent>
              </Tooltip>
            </div>
          ) : (
            <>
              {/* shim to maintain alignment */}
              <div />
            </>
          )}

          {/* Right side - Window controls (only for popover mode) */}
          {isPopoverMode && (
            <div className="flex flex-row gap-0.5 items-center justify-end">
              {onToggleFullscreen && (
                <Tooltip delayDuration={700}>
                  <TooltipTrigger asChild>
                    <IconButton onClick={onToggleFullscreen} variant="ghost">
                      {isFullscreen ? (
                        <Minimize className="size-4.5" />
                      ) : (
                        <Maximize className="size-4.5" />
                      )}
                    </IconButton>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>{isFullscreen ? "Exit Fullscreen" : "Enter Fullscreen"}</p>
                  </TooltipContent>
                </Tooltip>
              )}
              {onClose && (
                <IconButton onClick={onClose} variant="ghost">
                  <X className="size-4.5" />
                </IconButton>
              )}
            </div>
          )}
        </div>

        {/* Messages area */}
        <AssistantProvider api={api} conversationId={currentConversationId} contextId={contextId}>
          <ConversationViewer
            messages={messages}
            isLoading={isSendingMessage}
            isLoadingHistory={isLoadingHistory}
            currentConversationId={currentConversationId}
            isCreatingConversation={isCreatingConversation}
            debugMode={debugMode}
            agentMinHeight={agentMinHeight}
            handleMessageRef={handleMessageRef}
            scrollContainerRef={scrollContainerRef}
            contentRef={contentRef}
            contextTitle={contextTitle}
            emptyStateTitle={emptyStateTitle}
            emptyStateDescription={emptyStateDescription}
            onSubmitMessageFeedback={handleSubmitMessageFeedback}
            onClearMessageFeedback={clearMessageFeedback}
            optionalEmptyStateContent={optionalEmptyStateContent}
          />
        </AssistantProvider>
        {showPresetPrompts &&
          !isLoadingHistory &&
          !isCreatingConversation &&
          !isSendingMessage &&
          messages.length === 0 && (
            <PresetPrompts
              onPromptClick={handlePresetClick}
              disabled={!canSendMessage}
              prompts={presetPrompts}
            />
          )}

        {/* Input area - isolated component to prevent re-renders on typing */}
        <ChatInput
          ref={chatInputRef}
          contextId={contextId}
          currentConversationId={currentConversationId}
          isSendingMessage={isSendingMessage}
          isCancelling={isCancelling}
          mcpServers={mcpServers}
          selectedMcpServers={selectedMcpServers}
          onSendMessage={handleSendMessage}
          onCancelMessage={handleCancelMessage}
          placeholder={inputPlaceholder ?? `Ask anything about ${contextTitle}...`}
          showToolSelector={showToolSelector}
          onToggleMcpServer={handleMcpServerToggle}
          skillsEnabled={skillsEnabled}
          systemSkills={systemSkills}
          userSkills={userSkills}
          isLoadingSkills={isLoadingSkills}
          onCreateSkill={createSkill}
          onUpdateSkill={updateSkill}
          onDeleteSkill={deleteSkill}
          onGetSkill={getSkill}
          externalMcpAccessIds={externalMcpAccessIds}
        />
      </div>
    </div>
  );
}
